import omit from 'just-omit'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Select,
  SelectAction,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components-latest/primitives/Controls/Select'
import {
  formatPeriodValue,
  transformPeriodToRange
} from '@/components-latest/ui/PerformancePage/DateRangeSelect/DateRangeSelect.helpers'

import DateRangeModal from '../DateRangeModal'
import Styles from './DateRangeSelect.module.scss'

const PERIOD_MAP = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  'MONTHS-1': 'months-1',
  'MONTHS-3': 'months-3',
  CUSTOM: 'custom'
} as const

type PeriodValues = typeof PERIOD_MAP[keyof typeof PERIOD_MAP]
type CalendarRange = {
  startDate: Date
  endDate: Date
}

interface IDateRangeSelectProps {
  onChange: (range: CalendarRange) => void
  defaultValue: PeriodValues
}

const DateRangeSelect: React.FC<IDateRangeSelectProps> = ({
  onChange,
  defaultValue = PERIOD_MAP.DAILY
}) => {
  const { t } = useTranslation()

  const [calendarIsOpen, setCalendarIsOpen] = useState(false)
  const [selectIsOpen, setSelectIsOpen] = useState(false)

  const [{ period, range }, setValue] = useState<{
    period: PeriodValues
    range?: CalendarRange
  }>({ period: defaultValue })

  const updateValue = useCallback(
    (period: PeriodValues, range?: CalendarRange) => {
      setValue({ period: period, ...(range ? { range } : null) })

      const finalRange = transformPeriodToRange(period, range)
      onChange(finalRange)
    },
    [onChange]
  )

  const handleSelectChange = updateValue
  const handleCalendarPick = (startDate: Date | null, endDate: Date | null) => {
    if (startDate && endDate) {
      updateValue(PERIOD_MAP.CUSTOM, { startDate, endDate })
    }
  }
  const handleCustomRangeAction = () => {
    setSelectIsOpen(false)
    setCalendarIsOpen(true)
  }

  useEffect(
    function triggerChangeOnMount() {
      if (defaultValue) {
        handleSelectChange(defaultValue)
      }
    },
    [defaultValue, handleSelectChange]
  )

  return (
    <Select
      onValueChange={handleSelectChange}
      value={period}
      open={selectIsOpen}
      onOpenChange={open => setSelectIsOpen(() => open)}>
      <SelectTrigger className={Styles.wrapper}>
        <SelectValue className={Styles.trigger}>
          <div className={Styles.triggerContent}>
            {/* <CalendarFilled /> */}
            <span>{formatPeriodValue(period, range)}</span>
          </div>
        </SelectValue>
      </SelectTrigger>
      <SelectContent className={Styles.zIndex40}>
        {Object.values(omit(PERIOD_MAP, 'CUSTOM')).map(period => (
          <SelectItem key={period} value={period}>
            {t(`performance.range-picker.${period}`)}
          </SelectItem>
        ))}
        <SelectAction
          value={PERIOD_MAP.CUSTOM}
          onClick={handleCustomRangeAction}>
          {t('performance.range-picker.custom')}
        </SelectAction>
      </SelectContent>
      <DateRangeModal
        onApply={handleCalendarPick}
        startDate={range?.startDate || null}
        endDate={range?.endDate || null}
        isOpen={calendarIsOpen}
        toggleIsOpen={() => setCalendarIsOpen(state => !state)}
      />
    </Select>
  )
}

export { PERIOD_MAP }
export type { CalendarRange, PeriodValues }
export default DateRangeSelect
